import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, UserRound } from 'lucide-react';
import CommercialCells, { COMMERCIAL_LABELS } from '../components/CommercialCells';
import { EMPTY, PANEL, TD, TH } from '../components/tableStyles';
import { useReport } from '../ReportContext';
import { catalogTotals } from '../stats';
import { emptyStages } from '../adapt';
import type { CatalogMetrics, Filters, OpenDrill, StageKey, StageSplit, WorkRow } from '../types';

/** What each sortable column sorts by. Stage columns sort by value in rupees; Conv% is blank on this tab, so it is not sortable. */
const SORT_VALUE: Record<string, (m: CatalogMetrics) => number | null> = {
  'Orders': m => m.orders, 'Booked': m => m.booked,
  'Delivered': m => m.stages.delivered.value, 'In Transit': m => m.stages.inTransit.value,
  'Awaiting Pickup': m => m.stages.awaitingPickup.value, 'Returned': m => m.stages.returned.value, 'Cancelled': m => m.stages.cancelled.value,
  'Delivery %': m => catalogTotals(m).deliveryPct, 'In Transit %': m => catalogTotals(m).inTransitPct, 'RTO %': m => catalogTotals(m).rtoPct, 'AOV': m => catalogTotals(m).bookedAov,
};

interface Props { rows: WorkRow[]; filters: Filters; openDrill: OpenDrill }

/** Category › Product › Agent: what sold, in which category, and who sold it. */
const CategoryTab: React.FC<Props> = ({ filters, openDrill }) => {
  const { catalog: full } = useReport();
  // Category and Product are filtered here so changing them never reloads the report.
  const catalog = useMemo(() => full
    .filter(c => filters.category === 'all' || c.category === filters.category)
    .map(c => ({ ...c, products: c.products.filter(p => filters.product === 'all' || p.product === filters.product) }))
    .filter(c => c.products.length > 0), [full, filters.category, filters.product]);
  const [openCat, setOpenCat] = useState<Set<string>>(new Set());
  const [openProd, setOpenProd] = useState<Set<string>>(new Set());
  // key null = the server's order (largest revenue first). A click sorts every level by that column: categories, products inside them, agents inside those.
  const [sort, setSort] = useState<{ key: string | null; dir: 'asc' | 'desc' }>({ key: null, dir: 'desc' });
  const sortBy = (key: string) => setSort(p => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }));
  const arrow = (key: string) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '');
  function sortList<T extends CatalogMetrics>(list: T[], nameOf: (x: T) => string): T[] {
    if (!sort.key) return list;
    const get = SORT_VALUE[sort.key];
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sort.key === 'name') return dir * nameOf(a).localeCompare(nameOf(b));
      const x = get ? get(a) : null, y = get ? get(b) : null;
      if (x === null && y === null) return 0;
      if (x === null) return 1;                      // a blank always sinks to the bottom
      if (y === null) return -1;
      return dir * (x - y);
    });
  }
  const tree = useMemo(() => sortList(catalog, c => c.category).map(c => ({
    ...c, products: sortList(c.products, p => p.product).map(p => ({ ...p, agents: sortList(p.agents, a => a.agent) })),
  })), [catalog, sort]);   // eslint-disable-line react-hooks/exhaustive-deps

  const grand = useMemo(() => catalog.reduce((a, c) => ({
    orders: a.orders + c.orders, valid: a.valid + c.valid, cancelled: a.cancelled + c.cancelled, units: a.units + c.units,
    revenue: a.revenue + c.revenue, cancelledValue: a.cancelledValue + c.cancelledValue, workItems: a.workItems + c.workItems,
    booked: a.booked + c.booked,
    stages: (Object.keys(a.stages) as StageKey[]).reduce((st, k) => ({ ...st, [k]: { orders: a.stages[k].orders + c.stages[k].orders, value: a.stages[k].value + c.stages[k].value } }), {} as Record<StageKey, StageSplit>),
  }), { orders: 0, valid: 0, cancelled: 0, units: 0, revenue: 0, cancelledValue: 0, workItems: 0, booked: 0, stages: emptyStages() } as CatalogMetrics), [catalog]);

  if (catalog.length === 0) return <div className={EMPTY}>No category or product activity for this selection.</div>;

  const toggle = (set: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    set(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  /** Every count opens the orders behind it: this category / product / agent only, in the chosen delivery stage. */
  const cells = (m: CatalogMetrics, name: string, scope: { agent: string; category?: string; product?: string }) => (
    <CommercialCells t={catalogTotals(m)} drill={(status, what, total) =>
      openDrill({ title: `${name} — ${what}`, kind: 'orders', agent: scope.agent, bucket: 'all', total, orderStatus: status ?? 'all', category: scope.category, product: scope.product })} />
  );

  return (
    <div className="space-y-5">
      <div className={PANEL}>
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700">
          <p className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">Category</span> ›
            <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-medium text-sky-800">Product</span> ›
            <span className="flex items-center gap-1 rounded-full bg-violet-100 px-2.5 py-0.5 text-xs font-medium text-violet-800"><UserRound className="h-3 w-3" />Agent</span>
            <span className="ml-1 text-xs text-slate-700">click a row to open it</span>
          </p>
          <div className="flex gap-2 text-sm">
            <button type="button" className="rounded-lg px-2.5 py-1 text-violet-700 hover:bg-violet-50 dark:text-violet-300 dark:hover:bg-violet-400/10"
              onClick={() => { setOpenCat(new Set(catalog.map(c => c.category))); setOpenProd(new Set(catalog.flatMap(c => c.products.map(p => p.product)))); }}>Expand all</button>
            <button type="button" className="rounded-lg px-2.5 py-1 text-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
              onClick={() => { setOpenCat(new Set()); setOpenProd(new Set()); }}>Collapse all</button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead><tr className="bg-brandColor">
              <th onClick={() => sortBy('name')} className={`${TH} cursor-pointer select-none !text-white text-left whitespace-nowrap`}>Category / Product / Agent{arrow('name')}</th>
              {COMMERCIAL_LABELS.map(h => SORT_VALUE[h]
                ? <th key={h} onClick={() => sortBy(h)} className={`${TH} cursor-pointer select-none !text-white text-right whitespace-nowrap`}>{h}{arrow(h)}</th>
                : <th key={h} className={`${TH} !text-white text-right whitespace-nowrap`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {tree.map(c => {
                const cOpen = openCat.has(c.category);
                return (
                  <React.Fragment key={c.category}>
                    <tr className="cursor-pointer border-t border-slate-100 bg-orange-50/60 font-semibold hover:bg-orange-50 dark:border-slate-700 dark:bg-orange-400/5"
                      onClick={() => toggle(setOpenCat, c.category)}>
                      <td className={TD}><span className="flex items-center gap-1.5">
                        {cOpen ? <ChevronDown className="h-4 w-4 text-slate-600" /> : <ChevronRight className="h-4 w-4 text-slate-600" />}
                        {c.category}<span className="text-xs font-normal text-slate-600">{c.products.length} products</span></span></td>
                      {cells(c, c.category, { agent: 'all', category: c.category })}
                    </tr>
                    {cOpen && c.products.map(p => {
                      const pOpen = openProd.has(p.product);
                      return (
                        <React.Fragment key={p.product}>
                          <tr className="cursor-pointer border-t border-slate-50 bg-sky-50/50 hover:bg-sky-50 dark:border-slate-700/50 dark:bg-sky-400/5"
                            onClick={() => toggle(setOpenProd, p.product)}>
                            <td className={`${TD} pl-9 font-medium`}><span className="flex items-center gap-1.5">
                              {pOpen ? <ChevronDown className="h-4 w-4 text-slate-600" /> : <ChevronRight className="h-4 w-4 text-slate-600" />}
                              {p.product}<span className="text-xs font-normal text-slate-600">{p.agents.length} agents</span></span></td>
                            {cells(p, p.product, { agent: 'all', category: c.category, product: p.product })}
                          </tr>
                          {pOpen && p.agents.map(a => (
                            <tr key={`${p.product}-${a.agent}`} className="border-t border-slate-50 bg-violet-50/30 dark:border-slate-700/50 dark:bg-violet-400/5">
                              <td className={`${TD} pl-16`}><span className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-violet-400" />{a.agent}</span></td>
                              {cells(a, `${a.agent} · ${p.product}`, { agent: a.agent, category: c.category, product: p.product })}
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="border-t border-slate-200 bg-emerald-50/60 font-semibold dark:border-slate-600 dark:bg-emerald-400/5">
              <tr>
                <td className={`${TD} whitespace-nowrap`}>Total</td>
                <CommercialCells t={catalogTotals(grand)} drill={(status, what, total) =>
                  openDrill({ title: `All — ${what}`, kind: 'orders', agent: 'all', bucket: 'all', total, orderStatus: status ?? 'all' })} />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CategoryTab;
